import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import style from "./boards-grid-new.module.css";
import { Board, BoardState } from "shared/datamodel/schemas/board";
import { useRouter } from "next/router";
import useStateValue from "frontend/state/value";
import AppHeader from "frontend/app-header/app-header";
import { createBoardNew } from "frontend/services/boardsService";
import useAuthentication from "frontend/hooks/use-authentication";
import Modal from "frontend/modal/modal";
import MaxUsersPerBoardError from "frontend/error/max-users-per-board-error";
import StyledButton from "frontend/ui-components/styled-button";
import tracking from "frontend/tracking";
import consts from "shared/consts";
import EditSeatsModal from "frontend/modals/edit-seats-modal";
import { useAtom, useAtomValue } from "jotai";
import { isBannerNotificationOnAtom, appLoadingAtom } from "state-atoms/general-atoms";
import { BlankCanvasId, CanvasTemplate } from "shared/datamodel/schemas/canvas-template";
import TemplatesSectionNew from "./templates-section-new";
import Checkout from "frontend/billing/checkout";
import classNames from "classnames";
import { InviteMode, userTipsAtom } from "state-atoms";
import { ProjectInTeam, Team } from "shared/datamodel/schemas/team";
import CollaborationHeaderNew from "frontend/modals/collaboration/collaboration-header-new";
import { PortalID } from "frontend/modal/portal-types";
import { Account, PlanFeatureName, TipName } from "shared/datamodel/schemas";
import MoveCanvasModalNew from "frontend/modals/collaboration/move-canvas-modal-new";
import MoveCanvasToast from "frontend/ui-components/move-canvas-toast";
import usePlanFeatureValue from "frontend/hooks/use-plan-features";
import useModalDeepLink from "frontend/hooks/use-modal-deep-link";
import ModalManager, { ModalAction, ModalType } from "frontend/modals/modal-manager";
import { RESET } from "jotai/utils";
import { InstanceType } from "shared/datamodel/schemas/ability";
import useAbility from "frontend/hooks/use-ability";
import RestrictedUserModal from "frontend/modals/restricted-user-new-modal";
import InviteModalNew from "frontend/modals/invite-modal-new";
import RestrictedUserNoticeModal from "frontend/modals/restricted-user-notice-new-modal";
import { markTipSeen, markTipsSeen } from "frontend/api";
import { getPathPrefix } from "../utils/getPathPrefix";
import { useRecentSelected } from "frontend/boards-grid/search-section/hooks/use-recent-selected";
import SideMenuNew from "./side-menu/side-menu-new";
import BoardsBySection from "./boards-by-section";
import useBoards from "frontend/hooks/use-boards";
import { useBoardsCache } from "frontend/hooks/caching/use-boards-cache";
import AccountMembers from "frontend/modals/collaboration/account-members";
import ConfirmCreateCanvasModal from "./confirm-create-canvas-modal";
import { useTeamsWithProjects } from "frontend/hooks/caching/use-teams-with-projects";
import {
  BoardSectionType,
  BoardSection,
  MY_BOARDS_SECTION_ID,
  SHARED_WITH_ME_SECTION_ID,
  RECENT_BOARDS_SECTION_ID,
} from "shared/datamodel/schemas/board-section";
import WalkthroughTipPresenter from "frontend/walkthrough-tips/walkthrough-tip-presenter";
import HomePageRefactorAnnouncement from "./home-page-refactor-announcement";
import { WalkthroughTipType } from "frontend/walkthrough-tips/walkthrough-tip-model";
import {
  getLocalStorageItem,
  isLocalStorageAvailable,
  removeItemFromLocalStorage,
  setLocalStorageItem,
} from "utils/storage-utils";
import { useInstanceMembers } from "frontend/hooks/use-instance-members";
import MaxBoardsReachedStandalone from "frontend/icons/max-boards-reached-standalone";
import { CloseIcon } from "frontend/icons/close";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import UpgradeCTA from "frontend/ui-components/upgrade-cta/upgrade-cta";
import { useBoardGridModals } from "./use-board-grid-modals";
import { noop } from "utils/fn-utils";
import DowngradeAnnouncement from "./downgrade-announcement";
import useCheckoutFlow from "frontend/billing/use-checkout-flow";

export type GridError = {
  type: "limitReached";
  data: string | number | null;
};

export function BoardsGridNew({ showGridError }: { showGridError?: GridError | null }) {
  enum AdminModalType {
    inviteModal = 0,
    EditSeatsModal = 1,
  }

  const [{ user }, dispatch] = useStateValue();
  const [error, setError] = useState<JSX.Element | null>(null);
  const { isReady } = useAuthentication(true);
  const [adminModalTypeToShow, setAdminModalTypeToShow] = useState<AdminModalType | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useCheckoutFlow();
  const {
    showDowngradeNoticeModal,
    showRestrictedNoticedModal,
    setShowDowngradeNoticeModal,
    setShowRestrictedNoticeModal,
  } = useBoardGridModals();
  const [showAccountMembers, setShowAccountMembers] = useState(false);
  const [showHomePageRefactorTip, setShowHomePageRefactorTip] = useState(false);
  const [canvasToMove, setCanvasToMove] = useState<Board | null>(null);
  const [isAppLoading, setIsAppLoading] = useAtom(appLoadingAtom);
  const userTips = useAtomValue(userTipsAtom);
  const isBannerNotificationOn = useAtomValue(isBannerNotificationOnAtom);
  const [canvasMovingMessage, setCanvasMovingMessage] = useState<JSX.Element | null>(null);
  const { onSelected } = useRecentSelected();
  const [isMovingBoards, setIsMovingBoards] = useState(false);

  const [inviteSource, setInviteSource] = useState<string>("board-grid-app-header");
  const [selectedSection, setSelectedSection] = useState<BoardSection>({
    id: null,
    type: BoardSectionType.RECENT,
  });
  const [approveModalType, setApproveModalType] = useState<InstanceType | null>(null);
  const [createCanvasProps, setCreateCanvasProps] = useState<{
    template: Omit<CanvasTemplate, "data"> & { data?: Record<string, unknown> };
    inputProjectId?: string | null;
    inputTeamId?: string | null;
  } | null>();
  const inviteToAccountRef = useRef<HTMLDivElement>(null);
  const eventSent = useRef(false);
  const hasMarkedTipsRef = useRef(false);
  const { canCreateInstance } = useAbility();
  const { fetchTeamsWithProjects, teamsWithProjects, getProjectsParent } = useTeamsWithProjects();
  const teamsWithProjectsArray = teamsWithProjects ?? [];

  const instance = useMemo(() => {
    if (teamsWithProjects === null || teamsWithProjectsArray.length === 0) {
      return null;
    }
    if (selectedSection.type === BoardSectionType.PROJECT) {
      return teamsWithProjectsArray.reduce((acc, team) => {
        if (acc) return acc;
        return team.projects?.find((project) => project.urlId === selectedSection.id);
      }, undefined as ProjectInTeam | undefined);
    }
    return teamsWithProjectsArray.find((team) => team.urlId === selectedSection.id);
  }, [selectedSection, teamsWithProjects]);

  const instanceType =
    selectedSection.type === BoardSectionType.PROJECT ? InstanceType.Project : InstanceType.Workspace;

  const selectedTeamId = useMemo(
    () => (selectedSection.type === BoardSectionType.WORKSPACE && instance ? instance.documentId : null),
    [selectedSection.type, instance]
  );

  const selectedProjectId = useMemo(
    () => (selectedSection.type === BoardSectionType.PROJECT && instance ? instance.documentId : null),
    [selectedSection.type, instance]
  );

  const getUrlIdForInstanceId = useCallback(
    (documentId: string) => {
      return teamsWithProjectsArray.reduce((acc, team) => {
        if (acc) return acc;
        if (team.documentId === documentId) return team.urlId;
        return team.projects?.find((project) => project.documentId === documentId)?.urlId;
      }, undefined as string | undefined);
    },
    [teamsWithProjectsArray]
  );

  const { members } = useInstanceMembers(instance?.documentId ?? null, instanceType);

  const shouldShoWRestrictedModal = !user?.isSubscribedToCanvas;

  const maxAllowedCanvases = usePlanFeatureValue(PlanFeatureName.MAX_ALLOWED_CANVASES);
  const isNewAccounts = useFeatureFlag("new-accounts");
  const { setModalDeepLink } = useModalDeepLink();
  const isMondayUser = user?.account?.id === "5";

  const router = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { boards } = useBoards();
  const { getAllBoards } = useBoardsCache();
  const cachedBoards = getAllBoards();

  const shouldShowUpgradeRequiredModal = cachedBoards.length >= maxAllowedCanvases;

  // Helper function to convert a section type and optional ID to a URL path
  const sectionToPath = (section: BoardSectionType, id: string | null): string => {
    switch (section) {
      case BoardSectionType.PROJECT: {
        return id ? `/project/${id}` : "/";
      }
      case BoardSectionType.WORKSPACE: {
        return id ? `/workspace/${id}` : "/";
      }
      case BoardSectionType.MY_BOARDS: {
        return "/my-boards";
      }
      case BoardSectionType.SHARED_WITH_ME: {
        return "/shared-with-me";
      }
      case BoardSectionType.RECENT: {
        return "/";
      }
      default: {
        return "/";
      }
    }
  };

  const pathToSection = (pathParams: string[]): BoardSection => {
    const id = pathParams.length > 1 ? pathParams[1] : null;
    switch (pathParams[0]) {
      case "project": {
        return id ? { id, type: BoardSectionType.PROJECT } : pathToSection(["/"]);
      }
      case "workspace": {
        return id ? { id, type: BoardSectionType.WORKSPACE } : pathToSection(["/"]);
      }
      case "my-boards": {
        return { id: MY_BOARDS_SECTION_ID, type: BoardSectionType.MY_BOARDS };
      }
      case "shared-with-me": {
        return { id: SHARED_WITH_ME_SECTION_ID, type: BoardSectionType.SHARED_WITH_ME };
      }
      default: {
        return { id: RECENT_BOARDS_SECTION_ID, type: BoardSectionType.RECENT };
      }
    }
  };

  // Helper function to navigate to a path with shallow routing
  const navigateToSection = (section: BoardSectionType, id: string | null) => {
    if (section === BoardSectionType.RECENT) {
      // if we are navigating to the recent section, we need to remove the selectedSection from local storage
      removeItemFromLocalStorage("selectedSection");
    }
    let path = sectionToPath(section, id);
    if (window.location.hash) {
      path = path + window.location.hash;
    }
    router.push(path, undefined, { shallow: true });
  };

  useEffect(() => {
    fetchTeamsWithProjects();
  }, []);

  useEffect(() => {
    // Parse the params array when it's available
    if (!router.isReady) {
      return;
    }

    const params = router.query.params ?? [];
    const paramsArray = Array.isArray(params) ? params : params.split("/");

    if (isLocalStorageAvailable()) {
      const selectedSection = getLocalStorageItem<BoardSection>("selectedSection");
      const urlSection = pathToSection(paramsArray);
      if (urlSection.type !== BoardSectionType.RECENT) {
        // section from url always wins
        setSelectedSection(urlSection);
      } else if (selectedSection) {
        // no section from url, use the section from local storage
        navigateToSection(selectedSection.type, selectedSection.id);
      } else {
        // no section from url or local storage, set the recent section
        setSelectedSection({ id: null, type: BoardSectionType.RECENT });
      }
    }
  }, [router.isReady, router.query.params]);

  useEffect(() => {
    // handle the case the selected team or project does not exist anymore
    if (
      [BoardSectionType.PROJECT, BoardSectionType.WORKSPACE].includes(selectedSection.type) &&
      !instance &&
      teamsWithProjects &&
      teamsWithProjects.length > 0
    ) {
      navigateToSection(BoardSectionType.RECENT, null);
    }
  }, [selectedSection, instance, teamsWithProjects]);

  useEffect(() => {
    if (router && router.query.board_users_limit && router.query.maxAllowedCollaborators) {
      const boardName = decodeURIComponent(router.query.board_users_limit.toString());
      const maxAllowedCollaborators = decodeURIComponent(router.query.maxAllowedCollaborators.toString());
      router.query = {};
      setError(
        <MaxUsersPerBoardError
          boardName={boardName}
          onDismiss={() => {
            setError(null);
            navigateToSection(BoardSectionType.RECENT, null);
          }}
          maxAllowedCollaborators={Number.parseInt(maxAllowedCollaborators)}
        />
      );
    }
  }, [router]);

  useEffect(() => {
    if (!eventSent.current) {
      //avoid sending duplicate events, bah.
      tracking.trackEvent(consts.TRACKING_CATEGORY.PAGE_VIEWS, "boards_page_view", user?.account?.id, user?.id);
      tracking.trackAnalyticsEvent("app_page_view", {
        source: "home_page",
        id: user?.id,
        accountId: user?.account?.id,
      });
      eventSent.current = true;
    }
  }, []);

  function showUpgradeRequiredModal(numberOfCanvases: number) {
    if (numberOfCanvases === 0) {
      return null;
    }
    tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.CANVAS_LIMIT_REACHED);
    tracking.trackAnalyticsEvent("upgrade-modal-shown", {
      category: consts.TRACKING_CATEGORY.BILLING,
      stringValue: "max-boards-reached",
    });
    return (
      <Modal onDismissOnEscape={() => setError(null)} onClickBackground={() => setError(null)} dimBackground={true}>
        <div className={style.maxBoardsReachedModal}>
          <div className={style.closeIcon} onClick={() => setError(null)}>
            <CloseIcon />
          </div>
          <MaxBoardsReachedStandalone />
          <div className={style.title} data-testid="having-fun-title">
            <p>Having fun, are we??</p>
          </div>
          <div className={style.subtitle}>
            <p>
              You have reached the <strong>{numberOfCanvases} canvas</strong> limit for this WorkCanvas free version.
              Upgrade to get unlimited canvases.
            </p>
          </div>
          <div className={style.buttonContainer} data-testid="upgrade-canvas-button">
            <StyledButton
              title="Upgrade Canvas"
              onClick={() => {
                tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.UPGRADE_CTA);
                tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "upgrade-cta-click", "upgrade-board-card");
                setShowUpgradeModal({ name: "general", value: "" });
                setError(null);
              }}
              customStyle={{
                borderRadius: "6px",
                color: "#FFF",
                fontFamily: "Poppins",
                fontSize: "14px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                letterSpacing: "0.124px",
              }}
            />
          </div>
        </div>
      </Modal>
    );
  }

  useEffect(() => {
    if (showGridError) {
      switch (showGridError.type) {
        case "limitReached": {
          setError(showUpgradeRequiredModal(showGridError.data as number));
          break;
        }
        default: {
          break;
        }
      }
    }
  }, [showGridError]);

  //Hide the move canvas message after some interval
  useEffect(() => {
    let timeOut: NodeJS.Timeout;
    if (canvasMovingMessage) {
      timeOut = setTimeout(() => {
        setCanvasMovingMessage(null);
      }, 6000);
    }
    return () => timeOut && clearTimeout(timeOut);
  }, [canvasMovingMessage]);

  useEffect(() => {
    if (!user?.canInvite && adminModalTypeToShow === AdminModalType.inviteModal) {
      tracking.trackEvent(consts.TRACKING_CATEGORY.INVITE_ACTION, "no_permission_to_invite_modal", "invite-to-account");
    }
  }, [user?.canInvite, adminModalTypeToShow, AdminModalType.inviteModal]);

  useEffect(() => {
    if (isLocalStorageAvailable()) {
      setLocalStorageItem("selectedSection", selectedSection);
    }
  }, [selectedSection]);

  useEffect(() => {
    if (userTips.has(TipName.homePageRefactor) || isNewAccounts) {
      markHomePageTipsAsSeen();
      setShowHomePageRefactorTip(false);
    } else {
      setShowHomePageRefactorTip(true);
    }
  }, [userTips, isNewAccounts]);

  function onMyCanvasesClick() {
    navigateToSection(BoardSectionType.MY_BOARDS, null);
  }

  function onRecentCanvasesClick() {
    navigateToSection(BoardSectionType.RECENT, null);
  }

  function onSharedWithMeClick() {
    navigateToSection(BoardSectionType.SHARED_WITH_ME, null);
  }

  function onEditSeats() {
    setAdminModalTypeToShow(AdminModalType.EditSeatsModal);
  }

  function showNoPermissionToCreateModal() {
    if (shouldShoWRestrictedModal) {
      return <RestrictedUserModal trackingEvent={"home"} onDismiss={() => setError(null)} />;
    }
    return (
      <Modal dimBackground={true} onClickBackground={() => setError(null)}>
        <div className={style.contactYourAdmin}>
          <CollaborationHeaderNew
            title={`You are not an editor in this ${instanceType}`}
            subtitle={`You are viewer in this ${instanceType}, to have editing permissions and create more canvases, please request access from the ${instanceType} owner.`}
            onDismiss={() => setError(null)}
          />
          <StyledButton
            title={"Got it"}
            customStyle={{
              width: "107px",
              alignSelf: "flex-end",
              backgroundColor: "#1973ff",
              color: "#fff",
              border: "none",
              height: "32px",
              borderRadius: "6px",
              letterSpacing: "0.124px",
              fontFamily: "Poppins",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "normal",
              padding: "0 16px",
            }}
            onClick={() => setError(null)}
          />
        </div>
      </Modal>
    );
  }

  function getTeamAndProjectForNewCanvas({ teamId, projectId }: { projectId?: string | null; teamId?: string | null }) {
    if (projectId === null && teamId === null) {
      return { teamId: null, projectId: null };
    }
    const instanceType = projectId ? InstanceType.Project : InstanceType.Workspace;

    if (user && instance && !canCreateInstance(user, instance as Team, instanceType)) {
      setError(showNoPermissionToCreateModal());
      setIsAppLoading(false);
    }
    const finalTeamId = teamId ?? (projectId && getProjectsParent(projectId)?.documentId) ?? null;
    const finalProjectId = projectId ?? null;
    return { teamId: finalTeamId, projectId: finalProjectId };
  }

  async function handleCreateNewBoard(
    template: Omit<CanvasTemplate, "data"> & { data?: Record<string, unknown> },
    boardParentInstance: { teamId: string | null; projectId: string | null } | null,
    source: string
  ) {
    setIsAppLoading(true);
    if (template.upgradeRequired) {
      setModalDeepLink({ modalType: ModalType.PreviewTemplate, id: template.id, name: template.name });
      setIsAppLoading(false);
      return;
    }

    if (shouldShowUpgradeRequiredModal) {
      setError(showUpgradeRequiredModal(maxAllowedCanvases));
      setIsAppLoading(false);
      return;
    }
    try {
      if (boardParentInstance) {
        const { teamId, projectId } = boardParentInstance;
        const userName = user?.name.split(" ")[0];
        const name = template.id == BlankCanvasId ? (userName ? `${userName}'s Canvas` : "New Canvas") : template.name;
        const boardState = BoardState.templateNotSetUp;
        const board = await createBoardNew(name, boardState, template.id, projectId, teamId, dispatch);
        if (board) {
          tracking.trackAnalyticsEvent("new_canvas", {
            category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
            templateName: template.name,
          });

          window.sessionStorage.setItem(consts.SESSION_STORAGE_KEYS.FIRST_ACCESS, "true");
          if (template.data) {
            window.sessionStorage.setItem(
              consts.SESSION_STORAGE_KEYS.CREATED_TEMPLATE_DATA + board.documentId,
              JSON.stringify(template.data)
            );
          }

          if (board.createdFromTemplateId) {
            tracking.trackAnalyticsEvent("new_template_placed", {
              alias: template.alias,
              templateId: template.id,
              templateName: template.name,
              source,
            });
          }

          const boardRoute = board.createdFromTemplateId
            ? `/d/${board.documentId}`
            : `/d/${board.documentId}#show=templateLibrary`;

          await router.push(boardRoute);
        }
      }
    } catch (error: unknown) {
      if ((error as { response?: { status: number; data: { maxBoardsAllowed: number } } })?.response?.status === 406) {
        const typedError = error as { response: { data: { maxBoardsAllowed: number } } };
        setError(showUpgradeRequiredModal(typedError.response.data.maxBoardsAllowed));
      }
    } finally {
      setIsAppLoading(false);
    }
  }

  function createNewBoard({
    template,
    inputProjectId = null,
    inputTeamId = null,
    source,
  }: {
    template: Omit<CanvasTemplate, "data"> & { data?: Record<string, unknown> };
    inputProjectId: string | null;
    inputTeamId: string | null;
    source: string;
  }) {
    tracking.trackAnalyticsEvent("create-canvas-clicked", {
      category: consts.TRACKING_CATEGORY.CANVAS_ACTION,
      templateName: template?.name,
      source: source,
    });
    if (shouldShoWRestrictedModal) {
      setError(showNoPermissionToCreateModal());
      setIsAppLoading(false);
      return;
    }

    if (shouldShowUpgradeRequiredModal) {
      setError(showUpgradeRequiredModal(maxAllowedCanvases));
      setIsAppLoading(false);
      return;
    }

    const boardParentInstance = getTeamAndProjectForNewCanvas({ teamId: inputTeamId, projectId: inputProjectId });

    if ((boardParentInstance.teamId === null && boardParentInstance.projectId === null) || !(members.length > 1)) {
      handleCreateNewBoard(template, boardParentInstance, source);
    } else {
      setApproveModalType(boardParentInstance?.projectId ? InstanceType.Project : InstanceType.Workspace);
      setCreateCanvasProps({
        template,
        inputProjectId: boardParentInstance?.projectId,
        inputTeamId: boardParentInstance?.teamId,
      });
    }
  }

  function onChooseTemplate({ template }: { template: CanvasTemplate }) {
    setIsAppLoading(true);
    setModalDeepLink({ modalType: RESET });
    tracking.trackEvent(consts.TRACKING_CATEGORY.ADMIN_ACTION, "canvas_template_selected_main_screen", template.id);
    createNewBoard({
      template,
      inputProjectId: selectedProjectId,
      inputTeamId: selectedTeamId,
      source: "template-library",
    });
  }

  function handleModalAction(action: ModalAction) {
    if (action) {
      switch (action.type) {
        case "chooseTemplate": {
          onChooseTemplate(action.payload);
          break;
        }
      }
    }
  }

  function onInviteClick(title?: string) {
    const underScoredTitle = title ? `${title.toLowerCase().replaceAll(" ", "_")}_` : "";
    title && setInviteSource(underScoredTitle);
    tracking.trackEvent(
      consts.TRACKING_CATEGORY.ADMIN_ACTION,
      `${underScoredTitle}invite_button_clicked`,
      "invite_type",
      "account"
    );
    if (shouldShoWRestrictedModal) {
      setError(showNoPermissionToCreateModal());
    } else {
      setAdminModalTypeToShow(AdminModalType.inviteModal);
    }
  }

  function handleNoPermissionToCreate() {
    setError(showNoPermissionToCreateModal());
    setIsAppLoading(false);
  }

  function onCloseConfirmCreateCanvasModal() {
    setApproveModalType(null);
  }

  const markHomePageTipsAsSeen = () => {
    const requiredTips = [
      TipName.homePageWalkthrough,
      TipName.homePageRefactor,
      TipName.walkthroughHomePageSections,
      TipName.walkthroughTeamsAndProjects,
      TipName.walkthroughInviteToAccount,
      TipName.walkthroughInviteInShareCanvas,
    ];
    const shouldMarkTipsAsSeen = isNewAccounts && userTips.size > 0 && !requiredTips.every((tip) => userTips.has(tip));
    if (shouldMarkTipsAsSeen && !hasMarkedTipsRef.current) {
      hasMarkedTipsRef.current = true;
      markTipsSeen(requiredTips);
    }
  };

  function renderUpgradeCTA() {
    return (
      <UpgradeCTA
        onClick={() => {
          tracking.reportGoogleAnalyticsConversion(consts.GA_CONVERSION_TYPES.UPGRADE_CTA);
          tracking.trackEvent(consts.TRACKING_CATEGORY.BILLING, "upgrade-cta-click", "home-page-header");
          setShowUpgradeModal({ name: "general", value: "" });
        }}
      />
    );
  }

  return (
    <div
      id={"boards-container"}
      className={classNames(style.container, style.containerMonday, {
        [style.containerWithBanner]: isBannerNotificationOn,
        [style.noTemplatesContainer]: selectedSection.type !== BoardSectionType.RECENT,
      })}
    >
      <AppHeader
        isBoardHeader={false}
        onInviteClick={onInviteClick}
        onRestrictedClick={() => setError(showNoPermissionToCreateModal())}
        renderUpgradeCTA={renderUpgradeCTA}
        setFollow={noop}
      >
        <StyledButton
          dataTestId="invite-to-account-button"
          title="Invite"
          icon={getPathPrefix("/images/plus-icon.svg")}
          onClick={() => onInviteClick("invite-people-header-button")}
          customStyle={{
            borderRadius: "6px",
            color: "#FFF",
            fontFamily: "Poppins",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "normal",
            letterSpacing: "0.124px",
            height: "32px",
          }}
          ref={inviteToAccountRef}
        />
      </AppHeader>
      {adminModalTypeToShow !== null && (
        <Modal dimBackground={true}>
          {adminModalTypeToShow === AdminModalType.EditSeatsModal ? (
            <EditSeatsModal onDismiss={() => setAdminModalTypeToShow(null)} />
          ) : (
            <InviteModalNew
              instance={user?.account as Account}
              type={InstanceType.Account}
              onDismiss={() => setAdminModalTypeToShow(null)}
              onEditSeats={onEditSeats}
              inviteMode={InviteMode.Edit}
              source={inviteSource}
            />
          )}
        </Modal>
      )}
      <div
        className={style.contentBackground}
        style={{
          transform: "none",
        }}
      />
      <div className={classNames(style.left, { [style.leftWithBanner]: isBannerNotificationOn && isMondayUser })}>
        {
          <>
            <SideMenuNew
              selectedTeamId={selectedTeamId}
              setSelectedTeam={(team) => {
                navigateToSection(BoardSectionType.WORKSPACE, team.urlId);
              }}
              selectedProjectId={selectedProjectId}
              setSelectedProject={(id, urlId) => {
                urlId ??= getUrlIdForInstanceId(id);
                if (urlId) {
                  navigateToSection(BoardSectionType.PROJECT, urlId);
                }
              }}
              selectedSection={{ ...selectedSection, id: instance?.documentId ?? null }}
              createNewBoard={createNewBoard}
              onEditSeats={onEditSeats}
              onMyCanvasesClick={onMyCanvasesClick}
              onRecentCanvasesClick={onRecentCanvasesClick}
              onSharedWithMeClick={onSharedWithMeClick}
              handleNoPermissionToCreate={handleNoPermissionToCreate}
              setShowRestrictedModal={(show: boolean) => {
                if (show) {
                  setError(<RestrictedUserModal trackingEvent={"home"} onDismiss={() => setError(null)} />);
                }
              }}
              setShowTemplatesModal={(show: boolean) =>
                show
                  ? setModalDeepLink({ modalType: ModalType.TemplateLibrary })
                  : setModalDeepLink({ modalType: ModalType.None })
              }
              setIsMovingBoards={setIsMovingBoards}
            />
          </>
        }
      </div>
      <div
        className={style.templates}
        style={{
          transform: "none",
          pointerEvents: isAppLoading ? "none" : "auto",
        }}
      >
        {isReady && selectedSection.type === BoardSectionType.RECENT && (
          <TemplatesSectionNew
            onCreateBoard={(template) =>
              createNewBoard({
                template,
                inputProjectId: selectedProjectId,
                inputTeamId: selectedTeamId,
                source: "template-section",
              })
            }
            onPreviewClicked={(template) => {
              setModalDeepLink({ modalType: ModalType.PreviewTemplate, id: template.id, name: template.name });
            }}
            setShowTemplatesModal={(show) =>
              show
                ? setModalDeepLink({ modalType: ModalType.TemplateLibrary })
                : setModalDeepLink({ modalType: ModalType.None })
            }
          />
        )}
      </div>
      <div
        className={classNames(style.contentArea, {
          [style.noTemplates]: selectedSection.type !== BoardSectionType.RECENT,
        })}
        style={{
          transform: "none",
          pointerEvents: isAppLoading ? "none" : "auto",
        }}
      >
        {isReady && (
          <div
            className={style.contentArea}
            style={{
              transform: "none",
              pointerEvents: isAppLoading ? "none" : "auto",
            }}
          >
            <BoardsBySection
              section={{ ...selectedSection, id: instance?.documentId ?? null }}
              onSelected={(documentId) => {
                setIsAppLoading(true);
                onSelected(documentId);
              }}
              createNewBoard={createNewBoard}
              onDuplicateError={(number) => setError(showUpgradeRequiredModal(number))}
              setCanvasToMove={setCanvasToMove}
              selectedTeamId={selectedTeamId}
              selectedProjectId={selectedProjectId}
              onEditSeats={onEditSeats}
              onDeleteSelectedTeam={onRecentCanvasesClick}
              setSelectedTeam={(id: string) => {
                const urlId = getUrlIdForInstanceId(id);
                if (urlId) {
                  navigateToSection(BoardSectionType.WORKSPACE, urlId);
                }
              }}
              onDeleteSelectedProject={onRecentCanvasesClick}
              setSelectedProject={(id: string, urlId?: string) => {
                urlId ??= getUrlIdForInstanceId(id);
                if (urlId) {
                  navigateToSection(BoardSectionType.PROJECT, urlId);
                }
              }}
              isMovingBoards={isMovingBoards}
              setIsMovingBoards={setIsMovingBoards}
            />
          </div>
        )}
      </div>
      <div className={style.footer}>
        <div className={style.logo}>
          <span>By</span>
          <img src={getPathPrefix("/images/monday-logo.svg")} alt="" />
        </div>
        <a href="https://monday.com/l/legal/tos/">Terms of use</a>
        <a href="https://monday.com/l/privacy/privacy-policy/">Privacy policy</a>
      </div>
      <ModalManager
        setIsLoading={setIsAppLoading}
        handleModalAction={handleModalAction}
        onShowUpgradeModal={() => {
          setModalDeepLink({ modalType: RESET });
          setShowUpgradeModal({ name: "general", value: "" });
        }}
      />
      {error && <Modal dimBackground={true}>{error}</Modal>}
      {showUpgradeModal && user && (
        <Checkout
          user={user}
          onDismiss={() => {
            setShowUpgradeModal(null);
          }}
        />
      )}
      {canvasToMove && (
        <Modal dimBackground={true} onDismissOnEscape={() => setCanvasToMove(null)}>
          <MoveCanvasModalNew
            board={canvasToMove}
            onDismiss={() => setCanvasToMove(null)}
            setCanvasMovingMessage={setCanvasMovingMessage}
          />
        </Modal>
      )}
      {canvasMovingMessage && (
        <MoveCanvasToast content={canvasMovingMessage} onClose={() => setCanvasMovingMessage(null)} />
      )}
      {showDowngradeNoticeModal && (
        <Modal dimBackground={true}>
          <DowngradeAnnouncement
            onDismiss={() => setShowDowngradeNoticeModal(false)}
            onUpgrade={() => {
              setShowUpgradeModal({ name: "general", value: "" });
              setShowDowngradeNoticeModal(false);
            }}
          />
        </Modal>
      )}

      {showRestrictedNoticedModal && (
        <RestrictedUserNoticeModal
          trackingEvent={"home"}
          onDismiss={() => {
            markTipSeen(TipName.restrictedUsersNotice);
            setShowRestrictedNoticeModal(false);
          }}
        />
      )}
      {showAccountMembers && (
        <Modal>
          <AccountMembers
            title={"View account members"}
            onDismiss={() => setShowAccountMembers(false)}
            onEditSeats={onEditSeats}
          />
        </Modal>
      )}
      {approveModalType && (
        <Modal
          dimBackground={true}
          onClickBackground={onCloseConfirmCreateCanvasModal}
          onDismissOnEscape={onCloseConfirmCreateCanvasModal}
        >
          <ConfirmCreateCanvasModal
            type={approveModalType}
            onCancel={onCloseConfirmCreateCanvasModal}
            onCreate={() => {
              createCanvasProps &&
                handleCreateNewBoard(
                  createCanvasProps.template,
                  {
                    projectId: createCanvasProps.inputProjectId ?? null,
                    teamId: createCanvasProps.inputTeamId ?? null,
                  },
                  "confirm-create-canvas-modal"
                );
              onCloseConfirmCreateCanvasModal();
            }}
          />
        </Modal>
      )}
      {showHomePageRefactorTip && (
        <Modal dimBackground={true}>
          <HomePageRefactorAnnouncement
            onDismiss={() => {
              setShowHomePageRefactorTip(false);
            }}
          />
        </Modal>
      )}
      <div id={PortalID.Floaters} />
      <div id={PortalID.Tooltips} />
      <WalkthroughTipPresenter
        tipName={TipName.walkthroughInviteToAccount}
        attachToRef={inviteToAccountRef}
        side="top"
        type={WalkthroughTipType.HomePage}
      />
    </div>
  );
}
